import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const gbp = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 2 }).format(n || 0);

export default function PayInvoice() {
  const { id } = useParams();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/invoices/${id}/public`)
      .then((r) => setInv(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/invoices/${id}/checkout`, { origin_url: window.location.origin });
      if (!data.checkout_url) { toast.error("Could not start payment. Please try again."); setPaying(false); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2.5 mb-6 justify-center">
          <img src="/va-logo-orange.png" alt="VA Property Care" className="h-10 w-10 object-contain" />
          <span className="font-display text-lg font-bold text-slate-900">VA Property Care</span>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-lg" data-testid="pay-invoice-card">
          {loading && <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>}
          {notFound && <p className="text-center text-slate-500 py-8" data-testid="pay-invoice-notfound">This invoice could not be found.</p>}
          {inv && (
            <>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-600" /> Invoice</h1>
              <p className="text-sm text-slate-500 mt-1">For {inv.client_name} · {inv.property_name}</p>
              <p className="text-xs text-slate-400 mt-0.5">Dated {(inv.created_at || "").slice(0, 10)}</p>

              <div className="mt-5 divide-y divide-slate-100">
                {inv.lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm" data-testid="pay-invoice-line">
                    <span className="text-slate-700">{l.product}</span>
                    <span className="text-slate-600 font-mono text-xs">{inr(l.amount_inr)} · {gbp(l.amount_gbp)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between font-semibold text-slate-900">
                <span>Total</span>
                <span className="font-mono" data-testid="pay-invoice-total">{inr(inv.total_inr)} · {gbp(inv.total_gbp)}</span>
              </div>

              {inv.status === "paid" ? (
                <div className="mt-6 flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg py-3" data-testid="pay-invoice-paid">
                  <CheckCircle2 className="h-5 w-5" /> This invoice has been paid. Thank you!
                </div>
              ) : (
                <button onClick={pay} disabled={paying} data-testid="pay-invoice-button"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg px-6 py-3.5 transition-all active:scale-[0.98] disabled:opacity-60">
                  {paying && <Loader2 className="h-4 w-4 animate-spin" />} Pay {gbp(inv.total_gbp)}
                </button>
              )}
              {user
                ? <Link to="/portal/my-invoices" className="mt-4 block text-center text-sm text-slate-400 hover:text-slate-700">Back to My Invoices</Link>
                : <Link to="/" className="mt-4 block text-center text-sm text-slate-400 hover:text-slate-700">Back to home</Link>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
